import { Layout } from "@/components/layout/layout";
import { useGetCircuitLeaderboard } from "@workspace/api-client-react";
import { Trophy, Medal, MapPin } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { formatMoney } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ListEmpty, ListErrorBlock } from "@/components/list-state";
import { PlayerAvatar } from "@/components/player-avatar";

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
  if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
  if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
  return <span className="text-muted-foreground font-bold">{rank}</span>;
}

const LEADERBOARD_LIMIT = 50;

export default function Leaderboard() {
  useSeo({
    title: "Leaderboard & Standings",
    description:
      "Live Interclub Poker Circuit leaderboard. Track season standings, top players, and points across every league and venue.",
    canonicalPath: "/leaderboard",
  });
  const { data: fullLeaderboard = [], isLoading, isError, error, refetch } = useGetCircuitLeaderboard();
  const leaderboard = fullLeaderboard.slice(0, LEADERBOARD_LIMIT);

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground tracking-tight">Circuit Standings</h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
              The top 50 players across every league and venue on the circuit, ranked by combined points.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <div className="bg-background rounded-xl border border-border p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
          </div>
        ) : isError ? (
          <ListErrorBlock error={error} onRetry={() => refetch()} title="Couldn't load standings" />
        ) : leaderboard.length === 0 ? (
          <ListEmpty
            icon={<Trophy className="h-7 w-7" />}
            title="No standings yet"
            description="Standings will appear here once players finish in circuit tournaments."
          />
        ) : (
          <div className="bg-background rounded-xl border border-border overflow-hidden">
            {/* Desktop / tablet table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow className="border-border">
                    <TableHead className="w-16 text-center">Rank</TableHead>
                    <TableHead>Player</TableHead>
                    <TableHead>Home Venue</TableHead>
                    <TableHead className="text-right">Points</TableHead>
                    <TableHead className="text-center">Played</TableHead>
                    <TableHead className="text-center">Wins</TableHead>
                    <TableHead className="text-right">Winnings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.map((entry) => (
                    <TableRow key={entry.playerId} className="border-border hover:bg-muted/20" data-testid={`leaderboard-row-${entry.rank}`}>
                      <TableCell className="text-center font-bold">
                        <div className="flex items-center justify-center">
                          <RankBadge rank={entry.rank} />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <PlayerAvatar name={entry.playerName} src={entry.playerAvatarUrl} size={32} />
                          {entry.playerName}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.homeVenue ? (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                            {entry.homeVenue}
                          </span>
                        ) : (
                          <span className="italic text-muted-foreground/60">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-bold text-primary">{entry.totalPoints.toLocaleString()}</TableCell>
                      <TableCell className="text-center text-muted-foreground">{entry.totalTournaments}</TableCell>
                      <TableCell className="text-center text-muted-foreground">{entry.wins}</TableCell>
                      <TableCell className="text-right font-medium">{formatMoney(entry.totalWinnings)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile stacked cards */}
            <ul className="md:hidden divide-y divide-border" data-testid="leaderboard-mobile-list">
              {leaderboard.map((entry) => (
                <li
                  key={entry.playerId}
                  className="p-4 space-y-3"
                  data-testid={`leaderboard-card-${entry.rank}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <RankBadge rank={entry.rank} />
                    </div>
                    <PlayerAvatar name={entry.playerName} src={entry.playerAvatarUrl} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-foreground break-words">{entry.playerName}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {entry.homeVenue ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-primary/70 shrink-0" />
                            {entry.homeVenue}
                          </span>
                        ) : (
                          <span className="italic text-muted-foreground/60">No home venue</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-primary leading-tight">
                        {entry.totalPoints.toLocaleString()}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">pts</div>
                    </div>
                  </div>

                  <dl className="grid grid-cols-3 gap-2 text-sm pl-11">
                    <div className="flex flex-col">
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Played</dt>
                      <dd className="font-medium text-foreground">{entry.totalTournaments}</dd>
                    </div>
                    <div className="flex flex-col">
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Wins</dt>
                      <dd className="font-medium text-foreground">{entry.wins}</dd>
                    </div>
                    <div className="flex flex-col">
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Winnings</dt>
                      <dd className="font-medium text-foreground">{formatMoney(entry.totalWinnings)}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
